import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UserCog, Phone, Target, TrendingUp, Ticket, BarChart3, Users, Trash2, Info, Shield } from "lucide-react";
import { VentureCard, VentureCardContent } from "@/components/ui/venture-card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CloserRequestsTab } from "@/components/admin/CloserRequestsTab";
import { SalesTicketsTab } from "@/components/admin/SalesTicketsTab";
import { DemoMetricsTab } from "@/components/admin/DemoMetricsTab";
import { CloserAssignmentsTab } from "@/components/admin/CloserAssignmentsTab";
import { ClosersManagementTab } from "@/components/admin/ClosersManagementTab";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SetupWarning } from "@/components/ui/setup-warning";
import { PageTour } from "@/components/tour/PageTour";
import { salesOperationsSteps } from "@/lib/tour-steps";

export default function SalesOperations() {
  const [activeTab, setActiveTab] = useState("overview");
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [teamMetrics, setTeamMetrics] = useState({
    totalDemos: 0,
    avgConversion: 0,
    activeClosers: 0,
    weeklyTarget: 0,
  });
  const [vhTeamCount, setVhTeamCount] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Load metrics from Supabase
    const loadMetrics = async () => {
      const { data: demos } = await supabase.from('demos').select('id, outcome');
      const { data: closers } = await supabase.from('closers').select('id, status, weekly_target');
      const { data: requests } = await supabase.from('closer_requests').select('id, status');

      if (demos) {
        const closedDemos = demos.filter(d => d.outcome === 'closed').length;
        const conversionRate = demos.length > 0 ? (closedDemos / demos.length * 100) : 0;
        setTeamMetrics(prev => ({ ...prev, totalDemos: demos.length, avgConversion: Math.round(conversionRate) }));
      }

      if (closers) {
        const active = closers.filter(c => c.status === 'active').length;
        const totalTarget = closers.reduce((sum, c) => sum + (c.weekly_target || 0), 0);
        setTeamMetrics(prev => ({ ...prev, activeClosers: active, weeklyTarget: totalTarget }));
      }

      if (requests) {
        setPendingRequestsCount(requests.filter(r => r.status === 'pending').length);
      }
    };

    loadMetrics();
  }, []);

  useEffect(() => {
    supabase
      .from("vh_team_members")
      .select("id", { count: "exact", head: true })
      .eq("status", "active")
      .then(({ count }) => setVhTeamCount(count || 0));
  }, []);

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={salesOperationsSteps} tourId="sales-operations" />
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Sales Operations</h1>
        <p className="text-muted-foreground">Manage call team, performance metrics, and sales support</p>
      </div>

      {/* Team Metrics */}
      <div data-tour-id="team-metrics" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-secondary/20">
              <Phone className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <p className="text-3xl font-bold">{teamMetrics.totalDemos}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Total Demos</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>All demos scheduled across all clients</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-success/20">
              <TrendingUp className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-3xl font-bold">{teamMetrics.avgConversion}%</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Avg Conversion</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Average demo-to-close conversion rate across all closers</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/20">
              <UserCog className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-3xl font-bold">{teamMetrics.activeClosers}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Active Closers</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Number of closers currently assigned to at least one client</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-secondary/20">
              <Target className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <p className="text-3xl font-bold">{teamMetrics.weeklyTarget}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Weekly Target</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Combined weekly demo target across all active closers</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {vhTeamCount === 0 && (
        <SetupWarning message="No Venture Hills setters or closers added yet — manage your team in Roles & Permissions so attribution can match GHL setter/closer names." />
      )}

      {/* Team management CTA — consolidated into Roles & Permissions per phase 16.1 */}
      <VentureCard>
        <VentureCardContent className="flex items-start gap-4 py-6">
          <div className="p-3 rounded-lg bg-primary/10">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1">Manage Team in Roles & Permissions</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Venture Hills setters and closers (with or without portal logins) are now managed in Roles & Permissions.
            </p>
            <Button variant="default" onClick={() => navigate('/admin/roles-permissions')}>
              Open Roles & Permissions
            </Button>
          </div>
        </VentureCardContent>
      </VentureCard>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-tour-id="tabs">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="demo-metrics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Demo Metrics
          </TabsTrigger>
          <TabsTrigger value="closers" className="flex items-center gap-2">
            <UserCog className="w-4 h-4" />
            Assignments
          </TabsTrigger>
          <TabsTrigger value="closers-manage" className="flex items-center gap-2">
            <Trash2 className="w-4 h-4" />
            Manage Closers
          </TabsTrigger>
          <TabsTrigger value="tickets" className="relative flex items-center gap-2">
            <Ticket className="w-4 h-4" />
            Sales Tickets
            {openTicketsCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-destructive text-destructive-foreground text-xs rounded-full">
                {openTicketsCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="closer-requests" className="relative">
            Closer Requests
            {pendingRequestsCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-destructive text-destructive-foreground text-xs rounded-full">
                {pendingRequestsCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <CloserAssignmentsTab />
        </TabsContent>

        <TabsContent value="demo-metrics" className="mt-6">
          <DemoMetricsTab />
        </TabsContent>

        <TabsContent value="closers" className="mt-6">
          <CloserAssignmentsTab />
        </TabsContent>

        <TabsContent value="closers-manage" className="mt-6">
          <ClosersManagementTab />
        </TabsContent>

        <TabsContent value="tickets" className="mt-6">
          <SalesTicketsTab />
        </TabsContent>

        <TabsContent value="closer-requests" className="mt-6">
          <CloserRequestsTab />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
